import { apiSlice } from "./api-slice";
import {
  getListByNameOrPhoneNumber,
  getListByStatus,
} from "../../app/utils/functions";

const getOpportunityListByProjects = (projects: string[]) =>
  projects?.length ? `&project=${projects}` : "";

const getOpportunityListBySiteVisitDate = (siteVisitDate: string) =>
  siteVisitDate ? `&site_visit_date=${siteVisitDate}` : "";

const getOpportunityListByRating = (ratings: string[]) =>
  ratings?.length ? `&rating=${ratings.join(',')}` : "";

const getOpportunityListByFollowUpDate = (followUpDate: string) =>
  followUpDate ? `&follow_up_date=${followUpDate}` : "";

const decodeUrlValue = (value?: string) => {
  try {
    return decodeURIComponent(value || "");
  } catch (error) {
    return value || "";
  }
};

export const OpportunityApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOpportunityList: builder.query({
      query: ({
        paginationInfo,
        selectedStatus,
        selectedProjects,
        searchText,
        selectedSiteVisitDate,
        selectedRating,
        selectedFollowUpDate,
        userToken,
      }) => ({
        url: `bren_erp.v8.api.get_opportunities?page=${
          paginationInfo.page
        }&size=${paginationInfo.size}${getListByStatus(
          selectedStatus
        )}${getOpportunityListByProjects(
          selectedProjects
        )}${getListByNameOrPhoneNumber(searchText)}${getOpportunityListBySiteVisitDate(selectedSiteVisitDate)}${getOpportunityListByRating(selectedRating)}${getOpportunityListByFollowUpDate(selectedFollowUpDate)}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
      providesTags: ["opportunity"],
    }),
    createOpportunity: builder.mutation({
      query: ({ payload, userToken }) => ({
        url: `bren_erp.v8.api.create_site_visit_lead`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
      invalidatesTags: ["opportunity", "unitDetails"],
    }),
    getOpportunityDetails: builder.query({
      query: ({ opportunityId, userToken }) => ({
        url: `bren_erp.v8.api.get_opportunity?opportunity_id=${opportunityId}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
      providesTags: ["opportunity"],
    }),
    getOpportunitySourceList: builder.query({
      query: ({ userToken }) => ({
        url: `bren_erp.v8.api.get_opportunity_source`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    updateOpportunity: builder.mutation({
      query: ({ payload, userToken }) => ({
        url: `bren_erp.v8.api.update_opportunity`,
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
      invalidatesTags: ["opportunity", "unitDetails"],
    }),
    getOpportunitySubSourceList: builder.query({
      query: ({ userToken, source }) => ({
        url: `bren_erp.v8.api.get_lead_sub_source?lead_source=${source}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getOpportunitySiteVisitList: builder.query({
      query: ({ opportunityId, paginationInfo, userToken }) => ({
        url: `bren_erp.v8.api.get_site_visits?opportunity_id=${opportunityId}&page=${paginationInfo.page}&size=${paginationInfo.size}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
      providesTags: ["opportunitySiteVisit"],
    }),
    addOpportunitySiteVisit: builder.mutation({
      query: ({ payload, userToken }) => ({
        url: `bren_erp.v8.api.add_site_visit`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
      invalidatesTags: ["opportunitySiteVisit"],
    }),
    getStatusList: builder.query({
      query: ({ userToken, currentStatus }) => ({
        url: `bren_erp.v8.api.status_based_on_current_status?current_status=${currentStatus}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getChannelPartnerList: builder.query({
      query: ({ userToken }) => ({
        url: `bren_erp.v8.api.get_external_channel_partner`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getSalesStageList: builder.query({
      query: ({ userToken }) => ({
        url: `bren_erp.v8.api.sales_stages`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    updateOpportunityComments: builder.mutation({
      query: ({ userToken, payload }) => ({
        url: `bren_erp.v8.api.add_opportunity_comments`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
      invalidatesTags: ["OpportunityComments"],
    }),
    fetchOpportunityComments: builder.query({
      query: ({ userToken, opportunityId }) => ({
        url: `bren_erp.v8.api.get_opportunity_internal_comments?opportunity_id=${opportunityId}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
      providesTags: ["OpportunityComments"],
    }),
    getOpportunityFollowUp: builder.query({
      query: ({ userToken, opportunityId }) => ({
        url: `bren_erp.v8.api.get_opportunity_follow_up?opportunity_id=${opportunityId}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    updateOpportunityFollowUp: builder.mutation({
      query: ({ userToken, payload }) => ({
        url: `bren_erp.v8.api.change_opportunity_follow_up`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
    }),
    getOpportunityCheckin: builder.mutation({
      //checkin
      query: ({ payload, userToken }) => ({
        url: `bren_erp.v8.channel_partner_app.get_opportunity_checkin`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
    }),
    createOpportunityCheckin: builder.mutation({
      query: ({ payload, userToken }) => ({
        url: `bren_erp.v8.channel_partner_app.create_new_checkin`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
      invalidatesTags: ["opportunity", "unitDetails"],
    }),
    getOpportunityLostReasonHierarchy: builder.query({
      query: ({ userToken }) => ({
        url: `bren_erp.opportunity_lost.get_opportunity_lost_reason_hierarchy`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    markOpportunityAsLost: builder.mutation({
      query: ({ userToken, body }) => ({
        url: `bren_erp.v8.api.mark_opportunity_as_lost`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body,
      }),
    }),
    getAvailableSlots: builder.query({
      query: ({ userToken, project, source }) => ({
        url: `bren_erp.v8.channel_partner_app.get_available_slots_sales_app?project=${encodeURIComponent(
          project
        )}${source ? `&source=${encodeURIComponent(source)}` : ''}`,
        // url: `bren_erp.v8.channel_partner_app.get_available_slots?project=${encodeURIComponent(project)}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getSalesStages: builder.query({
      query: ({ userToken }) => ({
        url: `bren_erp.v8.api.sales_stages`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    markAsNotVisited: builder.mutation({
      query: ({ name, userToken }) => ({
        url: `bren_erp.v8.api.mark_as_not_visited`,
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: { name },
      }),
      invalidatesTags: ["opportunity"], // adjust if you need to refresh something
    }),
    getPreSalesOwner: builder.query({
      query: ({ userToken, project }) => ({
        url: `bren_erp.v8.api.get_my_oppurtunies_pre_sales_owner?project=${encodeURIComponent(
          project
        )}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getBookingProposalsForOpportunity: builder.query({
      query: ({ userToken, opportunityId }) => ({
        url: `bren_erp.v8.booking.get_booking_proposals_for_opportunity?opportunity_id=${encodeURIComponent(
          opportunityId
        )}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getBookingProposalsForOpportunityCount: builder.query({
      query: ({ userToken, opportunityId }) => ({
        url: `bren_erp.v8.booking.get_booking_proposals_for_opportunity_count?opportunity_id=${encodeURIComponent(
          opportunityId
        )}`,
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
      }),
    }),
    getCustomerInsightQuestionnaire: builder.query({
      query: ({ userToken, docname, contextDoctype, contextMode, contextDocname, requestKey }) => {
        const decodedContextDocname = decodeUrlValue(contextDocname);
        const cacheBust = requestKey ? `&_=${encodeURIComponent(String(requestKey))}` : "";

        return {
          url: `bren_erp.utils.customer_insight.get_customer_insight_questionnaire?docname=${encodeURIComponent(
            docname || ""
          )}&context_doctype=${encodeURIComponent(
            contextDoctype || ""
          )}&context_mode=${encodeURIComponent(
            contextMode || "edit"
          )}&context_docname=${encodeURIComponent(decodedContextDocname)}${cacheBust}`,
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `token ${userToken}`,
          },
        };
      },
    }),
    saveCustomerInsightQuestionnaire: builder.mutation({
      query: ({ userToken, payload }) => ({
        url: `bren_erp.utils.customer_insight.save_customer_insight_questionnaire`,
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `token ${userToken}`,
        },
        body: payload,
      }),
    }),
  }),
});

export const {
  useCreateOpportunityMutation,
  useGetOpportunityListQuery,
  useGetOpportunityDetailsQuery,
  useUpdateOpportunityMutation,
  useGetOpportunitySourceListQuery,
  useGetOpportunitySubSourceListQuery,
  useGetOpportunitySiteVisitListQuery,
  useAddOpportunitySiteVisitMutation,
  useGetStatusListQuery,
  useGetChannelPartnerListQuery,
  useGetSalesStageListQuery,
  useFetchOpportunityCommentsQuery,
  useUpdateOpportunityCommentsMutation,
  useGetOpportunityFollowUpQuery,
  useUpdateOpportunityFollowUpMutation,
  useGetOpportunityCheckinMutation, //checkin status
  useCreateOpportunityCheckinMutation, //checkIn/Out request
  useGetOpportunityLostReasonHierarchyQuery, //Lost details
  useMarkOpportunityAsLostMutation, //post lost data
  useLazyGetAvailableSlotsQuery, //get slots
  useLazyGetSalesStagesQuery, //get sales stages
  useMarkAsNotVisitedMutation, //mark as not visited
  useLazyGetPreSalesOwnerQuery, //get pre sales owner
  useLazyGetBookingProposalsForOpportunityQuery, //get booking proposals for opportunity
  useGetBookingProposalsForOpportunityCountQuery, //get booking proposals count for opportunity
  useLazyGetCustomerInsightQuestionnaireQuery,
  useSaveCustomerInsightQuestionnaireMutation,
} = OpportunityApiSlice;
